import { message } from "antd";
import { type SorterResult } from "antd/lib/table/interface";
import { type InjectedFormikProps } from "formik";
import { Component, useReducer } from "react";

import { BTCheckbox } from "@btnet/core-components";
import { BTPopover } from "@btnet/core-components/BTPopover";

import { CheckAllState, type JobStatusTypes } from "types/enum";

import { getCheckAllState } from "utilities/array/array";

import { BTCol } from "commonComponents/btWrappers/BTCol/BTCol";
import { BTFormItem } from "commonComponents/btWrappers/BTForm/BTForm";
import { BTRow } from "commonComponents/btWrappers/BTRow/BTRow";
import {
    type IJobAccess,
    type JobAccessEntity,
} from "commonComponents/entity/jobAccess/JobAccess/JobAccess.api.types";
import { JobAccessContainer } from "commonComponents/entity/jobAccess/JobAccess/JobAccessContainer";
import { BTLoading } from "commonComponents/utilities/BTLoading/BTLoading";
import { PageSection } from "commonComponents/utilities/PageSection/PageSection";

import { type IFilterHandler } from "entity/filters/Filter/Filter.api.handler";
import {
    type FilterEntity,
    FilterEntityType,
    type IFilterFormValues,
    mapFilterEntityToFormValues,
    type SelectedFilterItem,
} from "entity/filters/Filter/Filter.api.types";
import {
    defaultFilterState,
    type FilterActions,
    FilterReducer,
    type IFilterReducerState,
    SetUnsavedAppliedFilter,
    UpdateUnsavedChanges,
} from "entity/filters/Filter/Filter.reducer";
import { FilterDrawer } from "entity/filters/Filter/FilterDrawer";
import { type SavedFilterItem } from "entity/filters/SavedFilter/SavedFilter.api.types";
import { type IInternalUserHandler } from "entity/internalUser/InternalUserDetails/InternalUser.api.handler";
import { type IInternalUserFormValues } from "entity/internalUser/InternalUserDetails/InternalUser.api.types";
import { type JobsiteGroup } from "entity/internalUser/InternalUserDetails/InternalUser.api.types";
import { JobAccessTabHeader } from "entity/sub/SubVendor/tabs/JobAccessTab";

import "../../../sub/SubVendor/tabs/JobAccessTab.less";

interface IUserJobAccessProps {
    handler?: IInternalUserHandler;
    filterHandler?: IFilterHandler;
    userId: number;
    userIsArchived: boolean;
    disabled?: boolean;
    isSelf: boolean;
    inEditMode?: boolean;
    jobGroups: JobsiteGroup[];
    userName: string;
    onJobAccessChanged: (jobAccess: IJobAccess) => void;
    onToggleAllJobAccess: (jobAccess: IJobAccess[], checked: boolean) => void;
    onJobAccessOnRoleChange: (roleId: number, jobAccess?: JobAccessEntity) => JobAccessEntity;
    roleJobStatusAccess: JobStatusTypes[];
}

interface IUserJobAccessInternalProps extends IUserJobAccessProps {
    filterReducerState: IFilterReducerState;
    filterReducerDispatch: React.Dispatch<FilterActions>;
}

interface IInternalUserJobAccessState {
    filterEntity?: FilterEntity;
    filterValues?: IFilterFormValues;
    sortSettings?: SorterResult<IJobAccess>;
}

class UserJobAccess extends Component<
    InjectedFormikProps<IUserJobAccessInternalProps, IInternalUserFormValues>
> {
    state: Readonly<IInternalUserJobAccessState> = {
        filterEntity: undefined,
        filterValues: undefined,
        sortSettings: undefined,
    };

    componentDidMount = async () => {
        await this.loadJobsites();
    };

    componentDidUpdate = async (
        prevProps: InjectedFormikProps<IUserJobAccessInternalProps, IInternalUserFormValues>,
    ) => {
        const { userId, values } = this.props;
        if (prevProps.userId !== userId) {
            await this.loadJobsites();
        } else if (prevProps.values.jobAccess !== null && values.jobAccess === null) {
            await this.loadJobsites(this.state.filterValues);
        }
    };

    private loadJobsites = async (newFilterValues?: IFilterFormValues) => {
        try {
            let filters;

            if (!newFilterValues) {
                const filterResponse = await this.props.filterHandler!.get(
                    FilterEntityType.InternalUserDetailsJobsList,
                );
                const filterValues = mapFilterEntityToFormValues(filterResponse);
                filters = filterValues;
                this.setState({ filterEntity: filterResponse });
            } else {
                filters = newFilterValues;
            }

            let jobResponse = await this.props.handler!.getJobsitesForUser(
                this.props.userId,
                filters,
                this.props.values.addToAllExistingJobs,
            );
            jobResponse = this.props.onJobAccessOnRoleChange(this.props.values.role, jobResponse);
            this.props.setFieldValue("jobAccess", jobResponse);
        } catch (ex) {
            this.setState(() => {
                throw ex;
            });
        }
    };

    private handleChangeSortSettings = (
        sorter: SorterResult<IJobAccess> | SorterResult<IJobAccess>[],
    ) => {
        this.setState({ sortSettings: sorter });
    };

    private handleFilterSubmit = async (updatedFilterValues: IFilterFormValues) => {
        await this.loadJobsites(updatedFilterValues);
        this.setState({ filterValues: updatedFilterValues });
        void message.success("Results updated");
    };

    private setUnsavedAppliedFilter = (values: SelectedFilterItem[], isChanged: boolean) =>
        this.props.filterReducerDispatch(new SetUnsavedAppliedFilter(values, isChanged));

    private handleGrantAccessToExistingJobs = (jobAccess: IJobAccess[], checked: boolean) => {
        this.props.setFieldValue("addToAllExistingJobs", checked);

        const newJobAccess = {
            jobs: jobAccess.map((job) => {
                job.hasAccess = checked;
                if (job.hasNotificationAccess !== undefined) {
                    job.hasNotificationAccess = checked;
                }
                return job;
            }),
        };
        this.props.setFieldValue("jobAccess", newJobAccess);
        this.props.onToggleAllJobAccess(jobAccess, checked);
    };

    private updateUnsavedChanges = (
        newFilters: SelectedFilterItem[],
        filterEntity: FilterEntity,
        savedFilter: SavedFilterItem,
    ) =>
        this.props.filterReducerDispatch(
            new UpdateUnsavedChanges(newFilters, filterEntity, savedFilter),
        );

    render() {
        const { values, setFieldValue, filterReducerState } = this.props;
        const hasAccessToAllExistingJobsCheckState =
            this.props.values.jobAccess &&
            getCheckAllState(this.props.values.jobAccess.jobs, (job) => job.hasAccess === true);

        return (
            <div>
                <PageSection
                    className="JobAccessTab"
                    title={
                        <JobAccessTabHeader
                            filterButton={
                                this.state.filterEntity && (
                                    <FilterDrawer
                                        filterId={FilterEntityType.InternalUserDetailsJobsList}
                                        entity={this.state.filterEntity}
                                        onSubmit={this.handleFilterSubmit}
                                        filterTypeEntity={
                                            FilterEntityType.InternalUserDetailsJobsList
                                        }
                                        showSavedFilters={false}
                                        showSaveButton={false}
                                        appliedFilter={filterReducerState.appliedFilter}
                                        setUnsavedAppliedFilter={this.setUnsavedAppliedFilter}
                                        showHasUnsavedChanges={
                                            filterReducerState.showHasUnsavedChanges
                                        }
                                        appliedFiltersFromSavedFilter={
                                            filterReducerState.appliedFiltersFromSavedFilter
                                        }
                                        updateUnsavedChanges={this.updateUnsavedChanges}
                                    />
                                )
                            }
                        />
                    }
                >
                    {!values.jobAccess && <BTLoading displayMode="absolute" />}
                    {values.jobAccess && (
                        <>
                            <BTRow>
                                {this.props.userId === 0 && (
                                    <BTCol className="padding-right-lg">
                                        <BTFormItem
                                            label="Existing Jobs:"
                                            tooltip="Some Job Status may have restrictions based on the role"
                                        >
                                            <BTCheckbox
                                                id="userAccessExistingJobs"
                                                data-testid="userAccessExistingJobs"
                                                disabled={this.props.disabled}
                                                checked={
                                                    values.addToAllExistingJobs &&
                                                    (hasAccessToAllExistingJobsCheckState ===
                                                        CheckAllState.AllChecked ||
                                                        values.jobAccess.jobs.length === 0)
                                                }
                                                indeterminate={
                                                    hasAccessToAllExistingJobsCheckState !==
                                                        CheckAllState.AllChecked &&
                                                    values.addToAllExistingJobs &&
                                                    values.jobAccess.jobs.length > 0
                                                }
                                                onChange={(e) =>
                                                    this.handleGrantAccessToExistingJobs(
                                                        values.jobAccess!.jobs,
                                                        e.target.checked,
                                                    )
                                                }
                                            >
                                                Grant {this.props.userName} access to all existing
                                                jobs and templates
                                            </BTCheckbox>
                                        </BTFormItem>
                                    </BTCol>
                                )}
                                <BTCol>
                                    <BTFormItem
                                        label="New Jobs:"
                                        id="userAccessNewJobsFormItem"
                                        tooltip="Some Job Status may have restrictions based on the role"
                                    >
                                        <BTPopover
                                            disabled={!this.props.userIsArchived}
                                            content={
                                                <span>
                                                    This user is archived. They will not have access
                                                    to new jobs.
                                                </span>
                                            }
                                        >
                                            <BTCheckbox<IInternalUserFormValues>
                                                id="userAccessNewJobs"
                                                data-testid="userAccessNewJobs"
                                                disabled={
                                                    this.props.disabled || this.props.userIsArchived
                                                }
                                                checked={
                                                    values.userAccessNewJobs &&
                                                    !this.props.userIsArchived
                                                }
                                                onChange={this.props.handleChange}
                                            >
                                                Grant {this.props.userName} automatic access to new
                                                jobs and templates
                                            </BTCheckbox>
                                        </BTPopover>
                                    </BTFormItem>
                                </BTCol>
                            </BTRow>
                            <BTRow className="margin-top-md">
                                <BTCol flex="auto">
                                    <JobAccessContainer
                                        jobAccessEntity={values.jobAccess}
                                        setFieldValue={setFieldValue}
                                        onJobAccessChanged={this.props.onJobAccessChanged}
                                        onToggleAllJobAccess={this.props.onToggleAllJobAccess}
                                        disabled={this.props.disabled}
                                        isSelf={this.props.isSelf}
                                        jobGroups={this.props.jobGroups}
                                        sortSettings={this.state.sortSettings}
                                        onChangeSortSettings={this.handleChangeSortSettings}
                                        roleJobStatusAccess={this.props.roleJobStatusAccess}
                                        inEditMode={this.props.inEditMode}
                                    />
                                </BTCol>
                            </BTRow>
                        </>
                    )}
                </PageSection>
            </div>
        );
    }
}

export const InternalUserJobAccess: React.FC<
    InjectedFormikProps<IUserJobAccessProps, IInternalUserFormValues>
> = (props) => {
    const [state, dispatch] = useReducer(FilterReducer, defaultFilterState);
    return <UserJobAccess {...props} filterReducerState={state} filterReducerDispatch={dispatch} />;
};
